import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Edit3,
  FileText,
  Loader2,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

type Employee = {
  id: string;
  fullName: string;
};

type Changelog = {
  id: string;
  weekStart: string;
  weekEnd: string;
  generatedContent: string;
  editedContent: string | null;
  status: string;
  approvedBy: string | null;
  approvedAt: string | Date | null;
  publishedAt: string | Date | null;
  slackChannel: string | null;
  createdAt: string | Date | null;
  updatedAt: string | Date | null;
  approver?: Employee;
};

function statusLabel(status: string) {
  switch (status) {
    case "draft":
      return { label: "Rascunho", variant: "secondary" as const };
    case "pending_approval":
      return { label: "Pendente", variant: "outline" as const };
    case "approved":
      return { label: "Aprovado", variant: "default" as const };
    case "published":
      return { label: "Publicado", variant: "default" as const };
    default:
      return { label: status, variant: "secondary" as const };
  }
}

function formatDate(dateStr: string) {
  return new Date(`${dateStr}T12:00:00`).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getDefaultDateRange() {
  const now = new Date();
  const end = new Date(now);
  end.setDate(end.getDate() - end.getDay());
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  return {
    weekStart: start.toISOString().split("T")[0],
    weekEnd: end.toISOString().split("T")[0],
  };
}

export default function ChangelogsPage({ embedded }: { embedded?: boolean }) {
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  const { data: changelogs, isLoading } = useQuery(orpc.changelogs.list.queryOptions());

  const changelogsListQueryOptions = orpc.changelogs.list.queryOptions();

  const triggerMutation = useMutation({
    mutationFn: async (range: { weekStart: string; weekEnd: string }) => {
      return rpcClient.changelogs.trigger(range);
    },
    onSuccess: (data: any) => {
      if (data.success) {
        queryClient.invalidateQueries(changelogsListQueryOptions);
        toast({ title: "Changelog gerado com sucesso" });
        setTriggerDialogOpen(false);
      } else {
        toast({
          title: data.error || "Não foi possível gerar o changelog",
          variant: "destructive",
        });
      }
    },
    onError: () => {
      toast({ title: "Erro ao gerar changelog", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, editedContent }: { id: string; editedContent: string }) => {
      await rpcClient.changelogs.update({ id, editedContent });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(changelogsListQueryOptions);
      setEditingId(null);
      toast({ title: "Changelog atualizado" });
    },
    onError: () => {
      toast({ title: "Erro ao atualizar changelog", variant: "destructive" });
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      await rpcClient.changelogs.approve({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(changelogsListQueryOptions);
      toast({ title: "Changelog aprovado" });
    },
    onError: () => {
      toast({ title: "Erro ao aprovar changelog", variant: "destructive" });
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (id: string) => {
      await rpcClient.changelogs.publish({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(changelogsListQueryOptions);
      toast({ title: "Changelog publicado no Slack" });
    },
    onError: () => {
      toast({ title: "Erro ao publicar changelog", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await rpcClient.changelogs.remove({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(changelogsListQueryOptions);
      toast({ title: "Changelog excluído" });
    },
    onError: () => {
      toast({ title: "Erro ao excluir changelog", variant: "destructive" });
    },
  });

  const openEditDialog = (changelog: Changelog) => {
    setEditingId(changelog.id);
    setEditContent(changelog.editedContent || changelog.generatedContent);
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-[400px]"
        data-testid="loading-changelogs"
      >
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="changelogs-page">
      {!embedded && (
        <PageHeader
          title="Changelogs"
          description="Changelog semanal gerado automaticamente"
          icon={<FileText className="h-6 w-6 text-primary" />}
          titleTestId="text-changelogs-title"
        />
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {changelogs?.length || 0} changelog{(changelogs?.length || 0) !== 1 ? "s" : ""}
        </p>
        <Button
          onClick={() => {
            setDateRange(getDefaultDateRange());
            setTriggerDialogOpen(true);
          }}
          data-testid="button-trigger-changelog"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Gerar Changelog
        </Button>
      </div>

      {(!changelogs || changelogs.length === 0) && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground" data-testid="text-no-changelogs">
              Nenhum changelog gerado ainda. Configure o repositório GitHub nas Configurações e
              clique em "Gerar Changelog Agora".
            </p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {changelogs?.map((changelog) => {
          const { label, variant } = statusLabel(changelog.status);
          const isExpanded = expandedId === changelog.id;
          const content = changelog.editedContent || changelog.generatedContent;
          const canApprove =
            changelog.status === "draft" || changelog.status === "pending_approval";
          const canPublish =
            changelog.status === "approved" || changelog.status === "pending_approval";

          return (
            <Card key={changelog.id} data-testid={`card-changelog-${changelog.id}`}>
              <CardHeader
                className="cursor-pointer"
                onClick={() => setExpandedId(isExpanded ? null : changelog.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-primary" />
                    <div>
                      <CardTitle className="text-base">
                        Semana {formatDate(changelog.weekStart)} — {formatDate(changelog.weekEnd)}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {changelog.approver && `Aprovado por ${changelog.approver.fullName}`}
                        {changelog.publishedAt &&
                          ` · Publicado em ${new Date(changelog.publishedAt).toLocaleDateString("pt-BR")}`}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={variant} data-testid={`badge-status-${changelog.id}`}>
                      {label}
                    </Badge>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CardHeader>

              {isExpanded && (
                <CardContent className="space-y-4">
                  <div
                    className="bg-muted/50 rounded-lg p-4 whitespace-pre-wrap text-sm"
                    data-testid={`text-content-${changelog.id}`}
                  >
                    {content}
                  </div>

                  {changelog.status !== "published" && (
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(changelog);
                        }}
                        data-testid={`button-edit-${changelog.id}`}
                      >
                        <Edit3 className="h-4 w-4 mr-1" />
                        Editar
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm("Tem certeza que deseja excluir este changelog?")) {
                            deleteMutation.mutate(changelog.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        data-testid={`button-delete-${changelog.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Excluir
                      </Button>

                      {canApprove && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            approveMutation.mutate(changelog.id);
                          }}
                          disabled={approveMutation.isPending}
                          data-testid={`button-approve-${changelog.id}`}
                        >
                          {approveMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 mr-1" />
                          )}
                          Aprovar
                        </Button>
                      )}

                      {canPublish && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            publishMutation.mutate(changelog.id);
                          }}
                          disabled={publishMutation.isPending}
                          data-testid={`button-publish-${changelog.id}`}
                        >
                          {publishMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4 mr-1" />
                          )}
                          Publicar no Slack
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      <Dialog open={triggerDialogOpen} onOpenChange={setTriggerDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Gerar Changelog</DialogTitle>
            <DialogDescription>Selecione o período para gerar o changelog</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="weekStart">Data início</Label>
                <Input
                  id="weekStart"
                  type="date"
                  value={dateRange.weekStart}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, weekStart: e.target.value }))}
                  data-testid="input-week-start"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weekEnd">Data fim</Label>
                <Input
                  id="weekEnd"
                  type="date"
                  value={dateRange.weekEnd}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, weekEnd: e.target.value }))}
                  data-testid="input-week-end"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTriggerDialogOpen(false)}
              data-testid="button-cancel-trigger"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => triggerMutation.mutate(dateRange)}
              disabled={triggerMutation.isPending || !dateRange.weekStart || !dateRange.weekEnd}
              data-testid="button-confirm-trigger"
            >
              {triggerMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Gerar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingId} onOpenChange={(open) => !open && setEditingId(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Editar Changelog</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={16}
              className="font-mono text-sm"
              data-testid="textarea-edit-changelog"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingId(null)}
              data-testid="button-cancel-edit"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (editingId) {
                  updateMutation.mutate({ id: editingId, editedContent: editContent });
                }
              }}
              disabled={updateMutation.isPending}
              data-testid="button-save-edit"
            >
              {updateMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
